import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useBlocker } from 'react-router-dom';
import { History } from 'lucide-react';
import { useAuth } from './auth.js';
import { getLocalStore } from '../offline/store.js';
import { registerUpdateGuards, updateReloadInProgress } from './app-update.js';
import { Button, Modal } from '../components/ui.js';
import { Banner } from '../components/ui/Feedback.js';

interface Guard {
  dirty: () => boolean;
  flush: () => Promise<void>;
}
const NavigationContext = createContext<((id: string, guard: Guard) => () => void) | undefined>(
  undefined /* React 19 requires this argument. skipcq: JS-W1042 */,
);
export function DraftNavigationProvider({ children }: { children: ReactNode }) {
  const guards = useRef(new Map<string, Guard>());
  const register = useCallback((id: string, guard: Guard) => {
    guards.current.set(id, guard);
    return () => {
      guards.current.delete(id);
    };
  }, []);
  const blocker = useBlocker(() => [...guards.current.values()].some((guard) => guard.dirty()));
  // A new release never reloads the page over an unsaved form; when the user
  // asks for the update, the device copies are written first.
  useEffect(() => registerUpdateGuards({
    unsavedDrafts: () => [...guards.current.values()].some((guard) => guard.dirty()),
    flushDrafts: async () => {
      await Promise.all(
        [...guards.current.values()].filter((guard) => guard.dirty()).map((guard) => guard.flush()),
      );
    },
  }), []);
  return (
    <NavigationContext.Provider value={register}>
      {children}
      <Modal
        open={blocker.state === 'blocked'}
        title="Quitter la saisie ?"
        onClose={() => blocker.reset?.()}
      >
        <div className="stack">
          <p>
            Des modifications ne sont pas encore confirmées sur le serveur. Vous pouvez rester pour
            les enregistrer ; les copies disponibles se retrouveront à votre retour.
          </p>
          <Button onClick={() => blocker.reset?.()}>Rester sur cette page</Button>
          <Button
            variant="secondary"
            onClick={() => {
              Promise.all(
                [...guards.current.values()]
                  .filter((guard) => guard.dirty())
                  .map((guard) => guard.flush()),
              ).then(() => blocker.proceed?.());
            }}
          >
            Quitter la page
          </Button>
        </div>
      </Modal>
    </NavigationContext.Provider>
  );
}

const MAX_BYTES = 200_000;
const MAX_AGE = 7 * 86_400_000;
export function primitiveDraft<T extends Record<string, string | number | boolean | null>>(
  value: unknown,
  shape: T,
): value is T {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(shape).every((key) => {
      const field = (value as Record<string, unknown>)[key];
      return shape[key] === null
        ? field === null || typeof field === 'string'
        : typeof field === typeof shape[key];
    }),
  );
}
export function parseDraft<T>(
  raw: string | undefined,
  validate: (v: unknown) => v is T,
): T | undefined {
  if (!raw || raw.length > MAX_BYTES) return undefined;
  try {
    const envelope = JSON.parse(raw) as { version?: number; at?: number; value?: unknown };
    if (
      envelope.version !== 1 ||
      typeof envelope.at !== 'number' ||
      envelope.at > Date.now() + 60_000 ||
      Date.now() - envelope.at > MAX_AGE ||
      !validate(envelope.value)
    )
      return undefined;
    return envelope.value;
  } catch {
    return undefined;
  }
}

/**
 * Device backup, scoped to the authenticated user. Never a server-save confirmation.
 * External spaces (customer portal) have no internal session: they pass `owner`,
 * the identity of the person signed in there, so copies never mix between people.
 */
export function useDraftBackup<T>(options: {
  name: string;
  value: T;
  dirty: boolean;
  enabled?: boolean;
  owner?: string | undefined;
  /**
   * The copy is the record for now (no network, no server draft): leaving the
   * page or tab keeps it on the device without the « leave? » prompt. Read
   * when the user navigates, so a getter sees refs set since the last render.
   */
  leaveFreely?: () => boolean;
  /**
   * Name of an earlier copy of the same input (kept under another name before
   * the record existed). When this name holds nothing, that copy is moved here
   * and offered for recovery, unless it is exactly what the form already holds.
   */
  adoptFrom?: string | undefined;
  validate: (v: unknown) => v is T;
  restore: (v: T) => void;
}) {
  const { me } = useAuth();
  const register = useContext(NavigationContext);
  const guardId = useId();
  const owner = options.owner ?? (me && !me.support_session ? me.id : undefined);
  const key =
    owner && options.enabled !== false ? `draft.v1.${owner}.${options.name}` : undefined;
  const adoptKey =
    key && options.adoptFrom && options.adoptFrom !== options.name
      ? `draft.v1.${owner}.${options.adoptFrom}`
      : undefined;
  const [recovery, setRecovery] = useState<T>();
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const [error, setError] = useState<string>();
  const [loadedKey, setLoadedKey] = useState<string>();
  const chain = useRef(Promise.resolve());
  const bypass = useRef(false);
  const readable = useRef(false);
  const latest = useRef(options);
  latest.current = options;
  const write = (raw: string) => {
    if (!key) return Promise.resolve();
    const task = chain.current.then(async () => {
      const store = await getLocalStore();
      await store.putMeta(key, raw);
    });
    chain.current = task.catch(() => undefined);
    return task;
  };
  useEffect(() => {
    let active = true;
    setRecovery(undefined /* React 19 requires this argument. skipcq: JS-W1042 */);
    setStatus('idle');
    setError(undefined /* React 19 requires this argument. skipcq: JS-W1042 */);
    setLoadedKey(undefined /* React 19 requires this argument. skipcq: JS-W1042 */);
    bypass.current = false;
    readable.current = false;
    if (key)
      getLocalStore()
        .then(async (store) => {
          const raw = await store.getMeta(key);
          if (raw || !adoptKey) return raw;
          // The record now exists: its earlier copy follows it instead of being orphaned.
          const earlier = await store.getMeta(adoptKey);
          const value = parseDraft(earlier, latest.current.validate);
          if (earlier === undefined || value === undefined) return raw;
          await store.putMeta(adoptKey, '');
          // The form already holds exactly this input and saves it itself.
          if (JSON.stringify(value) === JSON.stringify(latest.current.value)) return raw;
          await store.putMeta(key, earlier);
          return earlier;
        })
        .then((raw) => {
          if (active) {
            readable.current = true;
            const value = parseDraft(raw, latest.current.validate);
            setRecovery(value);
            setLoadedKey(key);
            if (raw && value === undefined) write('').catch(() => undefined);
          }
        })
        .catch(() => {
          if (active) {
            setStatus('failed');
            setError('La sauvegarde locale est indisponible.');
            setLoadedKey(key);
          }
        });
    return () => {
      active = false;
    };
  }, [key, adoptKey]);
  const serialized = JSON.stringify(options.value);
  useEffect(() => {
    if (!key || loadedKey !== key || !readable.current || bypass.current || recovery !== undefined)
      return undefined;
    const raw = options.dirty
      ? JSON.stringify({ version: 1, at: Date.now(), value: JSON.parse(serialized) })
      : '';
    if (raw.length > MAX_BYTES) {
      setStatus('failed');
      setError('Le brouillon est trop volumineux pour la sauvegarde locale.');
      return undefined;
    }
    let active = true;
    setStatus(options.dirty ? 'saving' : 'idle');
    write(raw)
      .then(() => {
        if (active) {
          setStatus(options.dirty ? 'saved' : 'idle');
          setError(undefined /* React 19 requires this argument. skipcq: JS-W1042 */);
        }
      })
      .catch(() => {
        if (active) {
          setStatus('failed');
          setError('Sauvegarde locale impossible. Conservez cette page ouverte.');
        }
      });
    return () => {
      active = false;
    };
    // Values are serialized by callers; the write queue keeps the last revision last.
  }, [key, loadedKey, options.dirty, serialized, recovery]);
  const guard = useMemo(
    () => ({
      dirty: () => Boolean(key && latest.current.dirty && !latest.current.leaveFreely?.() && !bypass.current),
      flush: () => chain.current,
    }),
    [key],
  );
  useEffect(() => register?.(guardId, guard), [register, guardId, guard]);
  useEffect(() => {
    if (!key || !options.dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      // An update the user accepted reloads the page AFTER the device copy is
      // written: the browser's own « leave this page? » would only frighten.
      if (!bypass.current && !updateReloadInProgress()) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [key, options.dirty]);
  return {
    recovery,
    ready: !key || loadedKey === key,
    status,
    error,
    restore: () => {
      if (recovery !== undefined) latest.current.restore(recovery);
      setRecovery(undefined /* React 19 requires this argument. skipcq: JS-W1042 */);
    },
    discard: async () => {
      try {
        await write('');
        setRecovery(undefined /* React 19 requires this argument. skipcq: JS-W1042 */);
      } catch {
        setError('Impossible d’effacer la copie locale. Réessayez.');
      }
    },
    clear: async (allowNavigation = true) => {
      try {
        await write('');
      } catch {
        setError('L’ancienne copie locale n’a pas pu être effacée.');
      }
      bypass.current = allowNavigation;
      setRecovery(undefined /* React 19 requires this argument. skipcq: JS-W1042 */);
      setStatus('idle');
    },
    flush: () => chain.current,
  };
}

export function DraftNotice({ draft }: { draft: ReturnType<typeof useDraftBackup> }) {
  return (
    <>
      {draft.recovery !== undefined ? (
        <section className="draft-notice" aria-label="Saisie précédente à reprendre">
          <span className="draft-notice__icon" aria-hidden="true">
            <History size={20} />
          </span>
          <div className="draft-notice__text">
            <strong>Une saisie précédente est disponible sur cet appareil.</strong>
            <p>
              Vous aviez commencé à remplir ce formulaire sans l’enregistrer. Rien n’a été envoyé au
              serveur : la copie est restée ici.
            </p>
            <p className="draft-notice__hint">
              « Reprendre » remet les champs comme vous les aviez laissés. « Ignorer » efface cette
              copie et garde ce qui est affiché maintenant.
            </p>
          </div>
          <div className="draft-notice__actions">
            <Button onClick={draft.restore}>Reprendre la saisie</Button>
            <Button
              variant="ghost"
              onClick={() => {
                draft.discard().catch(() => undefined);
              }}
            >
              Ignorer cette copie
            </Button>
          </div>
        </section>
      ) : null}
      {draft.error ? <Banner tone="danger">{draft.error}</Banner> : null}
      {draft.status === 'saved' ? (
        <p className="draft-saved" role="status">
          Copie conservée sur cet appareil.
        </p>
      ) : null}
    </>
  );
}
